import {
  collection,
  doc,
  addDoc,
  getDoc,
  setDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore'
import { db, auth } from '@/js/firebase'
import { logger } from '@/services/loggerService'
import { deliverableService } from '@/services/deliverableService'

/** Convert Firestore Timestamp fields to millis for client-side usage */
function convertTimestamps(data) {
  const convert = (val) => val?.toMillis ? val.toMillis() : val
  const result = {
    blueprintWorkshopAt: convert(data.blueprintWorkshopAt)
  }
  // Convert nested setupChecklist timestamps
  if (data.setupChecklist) {
    result.setupChecklist = {}
    for (const [key, val] of Object.entries(data.setupChecklist)) {
      result.setupChecklist[key] = {
        ...val,
        completedAt: convert(val?.completedAt)
      }
    }
  }
  return result
}

/** Convert blueprint subcollection timestamp fields */
function convertSubcollectionTimestamps(data) {
  const convert = (val) => val?.toMillis ? val.toMillis() : val
  return {
    generatedAt: convert(data.generatedAt),
    clientEditedAt: convert(data.clientEditedAt)
  }
}

/**
 * Blueprints have two states: 'draft' (admin only) and 'complete' (client can
 * view, download and edit). Legacy values ('review' | 'changes_requested' |
 * 'approved') all meant "the client can see it", so they normalize to complete.
 * ponytail: read-time mapping instead of a migration script.
 */
export function normalizeBlueprintStatus(status) {
  return status && status !== 'draft' ? 'complete' : 'draft'
}

class ShowService {
  constructor() {
    this.collection = 'shows'
  }

  // ─── Show subscriptions ─────────────────────────

  /**
   * Subscribe to all shows (admin use)
   * @param {Function} callback - Called with array of shows on each update
   * @returns {Function} Unsubscribe function
   */
  subscribeToShows(callback) {
    return onSnapshot(
      collection(db, this.collection),
      (snapshot) => {
        const shows = []
        snapshot.forEach(docSnap => {
          const data = docSnap.data()
          shows.push({
            id: docSnap.id,
            ...data,
            ...convertTimestamps(data)
          })
        })
        logger.info('[ShowService] Shows snapshot', { count: shows.length })
        callback(shows)
      },
      (error) => {
        logger.firestoreListenerError('[ShowService] Shows snapshot error', error)
      }
    )
  }

  /**
   * Subscribe to shows for a specific company
   * @param {string} companyId
   * @param {Function} callback - Called with array of shows on each update
   * @returns {Function} Unsubscribe function
   */
  subscribeToCompanyShows(companyId, callback, onError) {
    const q = query(
      collection(db, this.collection),
      where('companyId', '==', companyId)
    )
    return onSnapshot(
      q,
      (snapshot) => {
        const shows = []
        snapshot.forEach(docSnap => {
          const data = docSnap.data()
          shows.push({
            id: docSnap.id,
            ...data,
            ...convertTimestamps(data)
          })
        })
        logger.info('[ShowService] Company shows snapshot', { companyId, count: shows.length })
        callback(shows)
      },
      (error) => {
        logger.firestoreListenerError('[ShowService] Company shows snapshot error', error, { companyId })
        if (typeof onError === 'function') onError(error)
      }
    )
  }

  /**
   * Get all shows for a company (one-shot read, not a subscription).
   * Used by the post-contract-create prompt to ask the admin which show to link.
   */
  async getCompanyShows(companyId) {
    try {
      const q = query(
        collection(db, this.collection),
        where('companyId', '==', companyId)
      )
      const snap = await getDocs(q)
      const shows = []
      snap.forEach(docSnap => {
        const data = docSnap.data()
        shows.push({
          id: docSnap.id,
          ...data,
          ...convertTimestamps(data)
        })
      })
      return shows
    } catch (error) {
      logger.error('[ShowService] Failed to get company shows', { error: error.message, companyId })
      throw error
    }
  }

  /**
   * Subscribe to a single show
   * @param {string} showId
   * @param {Function} callback - Called with show object (or null) on each update
   * @returns {Function} Unsubscribe function
   */
  subscribeToShow(showId, callback, onError) {
    const docRef = doc(db, this.collection, showId)
    return onSnapshot(
      docRef,
      (docSnap) => {
        if (!docSnap.exists()) {
          callback(null)
          return
        }
        const data = docSnap.data()
        const show = {
          id: docSnap.id,
          ...data,
          ...convertTimestamps(data)
        }
        logger.info('[ShowService] Show snapshot', { showId })
        callback(show)
      },
      (error) => {
        logger.error('[ShowService] Show snapshot error', { error: error.message, showId })
        if (typeof onError === 'function') onError(error)
      }
    )
  }

  // ─── Show CRUD ──────────────────────────────────

  /**
   * Create a new show
   */
  async createShow(showData) {
    try {
      const user = auth.currentUser
      if (!user) throw new Error('User not authenticated')

      const initialContractId = showData.contractId || null
      const newShow = {
        ...showData,
        logoUrl: showData.logoUrl || null,
        contractId: initialContractId,
        contractIds: initialContractId ? [initialContractId] : [],
        template: showData.template || { deliverables: [], events: [] },
        blueprintStatus: null,
        setupChecklist: {
          youtube: { completed: false, completedAt: null, completedBy: null },
          spotify: { completed: false, completedAt: null, completedBy: null },
          apple: { completed: false, completedAt: null, completedBy: null }
        }
      }

      const docRef = await addDoc(collection(db, this.collection), newShow)

      await this.logActivity(docRef.id, 'created', null, null, null, 'Show created')

      logger.info('[ShowService] Show created', { showId: docRef.id, companyId: showData.companyId })

      return { id: docRef.id, ...newShow }
    } catch (error) {
      logger.error('[ShowService] Failed to create show', { error: error.message })
      throw error
    }
  }

  /**
   * Update an existing show
   */
  async updateShow(showId, updates) {
    try {
      const user = auth.currentUser
      if (!user) throw new Error('User not authenticated')

      const docRef = doc(db, this.collection, showId)
      await updateDoc(docRef, updates)

      logger.info('[ShowService] Show updated', { showId, updates: Object.keys(updates) })
    } catch (error) {
      logger.error('[ShowService] Failed to update show', { error: error.message, showId })
      throw error
    }
  }

  /**
   * Copy the show's episode settings down onto its PRIMARY episode deliverables.
   *
   * Automation conditions read flat fields straight off the deliverable doc, and
   * one of the three evaluators (DeliverableActionButtons) is a synchronous Vue
   * computed — so a show-level value can't be joined at evaluation time and has
   * to physically live on the deliverable.
   *
   * Only `isPrimaryEpisodeDeliverable` deliverables are touched. The old
   * per-deliverable checkbox only ever appeared on those, and the live
   * "Post Queue Done (needs thumbnail)" rule has no episode guard — widening
   * this would route every deliverable of every episode to the thumbnail user.
   *
   * Explicit booleans on purpose: an absent field never matches `equals true`.
   *
   * ponytail: raw writeBatch instead of deliverableService.updateDeliverable —
   * that path fires automation per deliverable, and neither field is a rule
   * trigger. One batch caps this at 500 primary episodes per show; chunk if a
   * show ever gets that far.
   */
  async propagateEpisodeSettings(showId, { clientProvidesThumbnail, anthemDistributes } = {}) {
    try {
      const flags = {
        clientProvidesThumbnail: clientProvidesThumbnail === true,
        anthemDistributes: anthemDistributes !== false
      }

      const episodes = await getDocs(query(collection(db, 'projects'), where('showId', '==', showId)))
      if (episodes.empty) return 0

      const batch = writeBatch(db)
      let count = 0
      const changes = []
      for (const episode of episodes.docs) {
        // Filter in JS rather than adding isPrimaryEpisodeDeliverable to the query:
        // an episode has only a handful of deliverables, and this needs no index.
        const snap = await getDocs(query(collection(db, 'deliverables'), where('projectId', '==', episode.id)))
        snap.forEach((d) => {
          const data = d.data()
          if (data.isPrimaryEpisodeDeliverable !== true) return
          batch.update(d.ref, flags)
          count++
          for (const [field, newValue] of Object.entries(flags)) {
            if (data[field] !== newValue) changes.push({ id: d.id, field, oldValue: data[field] ?? null, newValue })
          }
        })
      }

      if (count > 0) await batch.commit()
      // Fire-and-forget; logActivity never throws.
      changes.forEach(({ id, field, oldValue, newValue }) => {
        deliverableService.logActivity(id, 'field_update', field, oldValue, newValue, 'Propagated from show settings')
      })
      logger.info('[ShowService] Propagated episode settings', { showId, deliverables: count, ...flags })
      return count
    } catch (error) {
      logger.error('[ShowService] Failed to propagate episode settings', { error: error.message, showId })
      throw error
    }
  }

  // ─── Contract chain ─────────────────────────────

  /**
   * Link a contract to a show's chain.
   * - Always appends `contractId` to `show.contractIds` (de-duplicated, preserves order).
   * - Backfills the current `show.contractId` into the chain if it's missing
   *   (handles legacy shows created before `contractIds` existed).
   * - If `setCurrent` is true, also updates `show.contractId` to the new value.
   *   Use case: post-create prompt after admin builds a fresh contract for a show.
   *   Leave false for back-filling old contracts that should stay as history only.
   */
  async linkContractToShow(showId, contractId, { setCurrent = false } = {}) {
    try {
      const user = auth.currentUser
      if (!user) throw new Error('User not authenticated')
      if (!contractId) throw new Error('contractId is required')

      const showRef = doc(db, this.collection, showId)
      const showSnap = await getDoc(showRef)
      if (!showSnap.exists()) throw new Error('Show not found')
      const show = showSnap.data()

      const existingChain = Array.isArray(show.contractIds) ? [...show.contractIds] : []
      // Backfill: include the current contractId if it's not already in the chain
      if (show.contractId && !existingChain.includes(show.contractId)) {
        existingChain.push(show.contractId)
      }
      // Append the new contract (deduped)
      if (!existingChain.includes(contractId)) {
        existingChain.push(contractId)
      }

      const updates = { contractIds: existingChain }
      if (setCurrent) updates.contractId = contractId

      await updateDoc(showRef, updates)

      const oldCurrent = show.contractId || null
      await this.logActivity(
        showId,
        setCurrent ? 'contract_renewed' : 'contract_linked',
        'contractId',
        oldCurrent,
        setCurrent ? contractId : oldCurrent,
        setCurrent ? 'New contract linked and set as current' : 'Contract linked to show history'
      )

      logger.info('[ShowService] Contract linked to show', { showId, contractId, setCurrent })
    } catch (error) {
      logger.error('[ShowService] Failed to link contract', { error: error.message, showId, contractId })
      throw error
    }
  }

  /**
   * Remove a contract from a show's chain (history only — does not affect episodes).
   * Refuses to remove the show's current contractId; admin must set another current first.
   */
  async unlinkContractFromShow(showId, contractId) {
    try {
      const user = auth.currentUser
      if (!user) throw new Error('User not authenticated')

      const showRef = doc(db, this.collection, showId)
      const showSnap = await getDoc(showRef)
      if (!showSnap.exists()) throw new Error('Show not found')
      const show = showSnap.data()

      if (show.contractId === contractId) {
        throw new Error('Cannot remove the current contract. Set another as current first.')
      }

      const existingChain = Array.isArray(show.contractIds) ? show.contractIds : []
      const newChain = existingChain.filter(id => id !== contractId)

      await updateDoc(showRef, { contractIds: newChain })

      await this.logActivity(
        showId,
        'contract_unlinked',
        'contractIds',
        contractId,
        null,
        'Contract removed from show history'
      )

      logger.info('[ShowService] Contract unlinked from show', { showId, contractId })
    } catch (error) {
      logger.error('[ShowService] Failed to unlink contract', { error: error.message, showId, contractId })
      throw error
    }
  }

  /**
   * Self-heal: walk forward from the show's current contract via
   * `renewedToContractId` pointers (set by the auto-renewal Cloud Function and
   * any future manual chain) and advance the show to the latest contract,
   * appending every encountered id to `contractIds`.
   *
   * Catches the case where auto-renewal happened before this feature shipped
   * — the show is still pointing at an old, now-inactive contract, and we
   * walk it forward to the active terminal contract on first page load.
   *
   * Idempotent: short-circuits when there's nothing to advance.
   * Returns the resolved current contractId (whether changed or not).
   */
  async maybeAdvanceRenewalChain(showId) {
    try {
      const showRef = doc(db, this.collection, showId)
      const showSnap = await getDoc(showRef)
      if (!showSnap.exists()) return null
      const show = showSnap.data()
      if (!show.contractId) return null

      const chain = Array.isArray(show.contractIds) ? [...show.contractIds] : []
      if (!chain.includes(show.contractId)) chain.push(show.contractId)

      let cursor = show.contractId
      let advanced = false
      const visited = new Set([cursor])

      // Walk forward through renewedToContractId pointers
       
      while (true) {
        const cursorSnap = await getDoc(doc(db, 'contracts', cursor))
        if (!cursorSnap.exists()) break
        const next = cursorSnap.data().renewedToContractId
        if (!next || visited.has(next)) break
        cursor = next
        visited.add(cursor)
        if (!chain.includes(cursor)) chain.push(cursor)
        advanced = true
      }

      const startedWithChain = Array.isArray(show.contractIds) ? show.contractIds : []
      const chainChanged = chain.length !== startedWithChain.length
      if (!advanced && !chainChanged) return show.contractId

      const updates = { contractIds: chain }
      if (advanced) updates.contractId = cursor
      await updateDoc(showRef, updates)

      if (advanced) {
        await this.logActivity(
          showId,
          'contract_chain_advanced',
          'contractId',
          show.contractId,
          cursor,
          'Renewal chain auto-advanced to latest contract'
        )
      }

      logger.info('[ShowService] Renewal chain advanced', {
        showId, advanced, finalContractId: cursor, chainLength: chain.length
      })
      return cursor
    } catch (error) {
      // Self-heal must never block a page load — log and let caller continue
      logger.warn('[ShowService] maybeAdvanceRenewalChain failed', { error: error.message, showId })
      return null
    }
  }

  /**
   * Set a different contract from the chain as the show's current contract.
   * Future episodes inherit this contractId; existing episodes keep their snapshot.
   */
  async setCurrentContract(showId, contractId) {
    try {
      const user = auth.currentUser
      if (!user) throw new Error('User not authenticated')

      const showRef = doc(db, this.collection, showId)
      const showSnap = await getDoc(showRef)
      if (!showSnap.exists()) throw new Error('Show not found')
      const show = showSnap.data()

      const existingChain = Array.isArray(show.contractIds) ? show.contractIds : []
      if (!existingChain.includes(contractId)) {
        throw new Error('Contract is not linked to this show. Link it first.')
      }
      if (show.contractId === contractId) return

      const oldCurrent = show.contractId || null
      await updateDoc(showRef, { contractId })

      await this.logActivity(
        showId,
        'contract_current_changed',
        'contractId',
        oldCurrent,
        contractId,
        'Current contract changed'
      )

      logger.info('[ShowService] Current contract changed', { showId, oldCurrent, contractId })
    } catch (error) {
      logger.error('[ShowService] Failed to set current contract', { error: error.message, showId, contractId })
      throw error
    }
  }

  /**
   * Delete a show and its subcollection docs
   */
  async deleteShow(showId) {
    try {
      // Every subcollection a show can carry. Firestore does not cascade — a doc
      // deleted with live subcollections leaves them orphaned and unreachable,
      // so each one is enumerated and cleared explicitly.
      const SUBCOLLECTIONS = [
        'blueprint',
        'questionnaire',
        'discussionGuide',
        'episodeContent',
        'activityLogs',
        'seasons'
      ]

      const deletions = []
      for (const sub of SUBCOLLECTIONS) {
        const snap = await getDocs(collection(db, this.collection, showId, sub))
        snap.forEach(subDoc => deletions.push(deleteDoc(subDoc.ref)))
      }

      if (deletions.length) await Promise.all(deletions)

      // Delete the main doc
      const docRef = doc(db, this.collection, showId)
      await deleteDoc(docRef)

      logger.info('[ShowService] Show deleted', { showId, subDocsDeleted: deletions.length })
    } catch (error) {
      logger.error('[ShowService] Failed to delete show', { error: error.message, showId })
      throw error
    }
  }

  // ─── Blueprint subcollection ────────────────────

  /**
   * Get blueprint data for a show
   */
  async getBlueprint(showId) {
    try {
      const docRef = doc(db, this.collection, showId, 'blueprint', 'current')
      const docSnap = await getDoc(docRef)
      if (!docSnap.exists()) return null
      const data = docSnap.data()
      return { ...data, ...convertSubcollectionTimestamps(data) }
    } catch (error) {
      logger.error('[ShowService] Failed to get blueprint', { error: error.message, showId })
      throw error
    }
  }

  /**
   * Subscribe to blueprint subcollection (real-time)
   */
  subscribeToBlueprint(showId, callback, onError) {
    const docRef = doc(db, this.collection, showId, 'blueprint', 'current')
    return onSnapshot(
      docRef,
      (docSnap) => {
        if (!docSnap.exists()) {
          callback(null)
          return
        }
        const data = docSnap.data()
        callback({ ...data, ...convertSubcollectionTimestamps(data) })
      },
      (error) => {
        logger.error('[ShowService] Blueprint snapshot error', { error: error.message, showId })
        if (typeof onError === 'function') onError(error)
      }
    )
  }

  /**
   * Save blueprint draft to subcollection
   */
  async saveBlueprint(showId, { blueprintMarkdown, blueprintTranscript, blueprintPromptOverride }) {
    try {
      const user = auth.currentUser
      if (!user) throw new Error('User not authenticated')

      const blueprintRef = doc(db, this.collection, showId, 'blueprint', 'current')
      const showRef = doc(db, this.collection, showId)

      // Merge into subcollection doc
      await setDoc(blueprintRef, {
        markdown: blueprintMarkdown || '',
        transcript: blueprintTranscript || '',
        promptOverride: blueprintPromptOverride || null
      }, { merge: true })

      // Only set status to draft on first save — don't reset status
      // when admin is just tweaking content after approval/review
      const showDoc = await getDoc(showRef)
      if (!showDoc.exists() || !showDoc.data().blueprintStatus) {
        await updateDoc(showRef, { blueprintStatus: 'draft' })
      }

      await this.logActivity(showId, 'blueprint_saved', 'blueprint', null, null, 'Blueprint draft saved')

      logger.info('[ShowService] Blueprint saved', { showId })
    } catch (error) {
      logger.error('[ShowService] Failed to save blueprint', { error: error.message, showId })
      throw error
    }
  }

  /**
   * Set blueprint status (admin). Moving draft -> complete snapshots the
   * current markdown as our original version, so the client's later edits can
   * always be rolled back.
   */
  async setBlueprintStatus(showId, status) {
    try {
      const user = auth.currentUser
      if (!user) throw new Error('User not authenticated')

      const newStatus = normalizeBlueprintStatus(status)
      const showRef = doc(db, this.collection, showId)
      const blueprintRef = doc(db, this.collection, showId, 'blueprint', 'current')

      const showSnap = await getDoc(showRef)
      const previousStatus = normalizeBlueprintStatus(showSnap.data()?.blueprintStatus)
      if (previousStatus === newStatus) return

      const batch = writeBatch(db)
      batch.update(showRef, { blueprintStatus: newStatus })

      if (newStatus === 'complete') {
        // Snapshot ours as the original. Client edits are already discarded at
        // this point (admins must drop to draft to edit), so clear the markers.
        const blueprintSnap = await getDoc(blueprintRef)
        batch.set(blueprintRef, {
          ogMarkdown: blueprintSnap.data()?.markdown || '',
          clientEditedAt: null,
          clientEditedBy: null
        }, { merge: true })
      }

      await batch.commit()

      await this.logActivity(
        showId, 'blueprint_status_changed', 'blueprintStatus',
        previousStatus, newStatus,
        newStatus === 'complete' ? 'Blueprint marked complete' : 'Blueprint moved back to draft'
      )

      logger.info('[ShowService] Blueprint status set', { showId, newStatus })
    } catch (error) {
      logger.error('[ShowService] Failed to set blueprint status', { error: error.message, showId })
      throw error
    }
  }

  /**
   * Restore our original blueprint over the client's edited version (admin)
   */
  async restoreOriginalBlueprint(showId) {
    try {
      const user = auth.currentUser
      if (!user) throw new Error('User not authenticated')

      const blueprintRef = doc(db, this.collection, showId, 'blueprint', 'current')
      const blueprintSnap = await getDoc(blueprintRef)
      const ogMarkdown = blueprintSnap.data()?.ogMarkdown
      if (!ogMarkdown) throw new Error('No original version stored for this blueprint')

      await setDoc(blueprintRef, {
        markdown: ogMarkdown,
        clientEditedAt: null,
        clientEditedBy: null
      }, { merge: true })

      await this.logActivity(showId, 'blueprint_restored', 'blueprint', null, null, 'Original blueprint restored')

      logger.info('[ShowService] Blueprint original restored', { showId })
    } catch (error) {
      logger.error('[ShowService] Failed to restore original blueprint', { error: error.message, showId })
      throw error
    }
  }

  /**
   * Save a client's edited blueprint. The edited copy becomes the live version
   * (it is what the discussion guide generator reads).
   */
  async saveClientBlueprint(showId, markdown, { previousMarkdown, hasOriginal } = {}) {
    try {
      const user = auth.currentUser
      if (!user) throw new Error('User not authenticated')

      const blueprintRef = doc(db, this.collection, showId, 'blueprint', 'current')
      const payload = {
        markdown: markdown || '',
        clientEditedAt: serverTimestamp(),
        clientEditedBy: user.uid
      }
      // Safety net for blueprints completed before originals were snapshotted.
      // Rules allow ogMarkdown to be written once and never overwritten.
      if (!hasOriginal) payload.ogMarkdown = previousMarkdown || ''

      await setDoc(blueprintRef, payload, { merge: true })

      await this.logActivity(showId, 'blueprint_client_edited', 'blueprint', null, null, 'Client edited the blueprint')

      logger.info('[ShowService] Client blueprint saved', { showId })
    } catch (error) {
      logger.error('[ShowService] Failed to save client blueprint', { error: error.message, code: error.code, showId })
      throw error
    }
  }

  // ─── Setup checklist ───────────────────────────

  /**
   * Update a single setup checklist item (client-facing)
   * @param {string} showId
   * @param {string} stepKey - 'youtube' | 'spotify' | 'apple'
   * @param {boolean} completed
   */
  async updateSetupChecklistItem(showId, stepKey, completed) {
    try {
      const user = auth.currentUser
      if (!user) throw new Error('User not authenticated')

      const showRef = doc(db, this.collection, showId)
      await updateDoc(showRef, {
        [`setupChecklist.${stepKey}.completed`]: completed,
        [`setupChecklist.${stepKey}.completedAt`]: completed ? serverTimestamp() : null,
        [`setupChecklist.${stepKey}.completedBy`]: completed ? user.uid : null
      })

      await this.logActivity(
        showId,
        completed ? 'setup_checklist_completed' : 'setup_checklist_unchecked',
        `setupChecklist.${stepKey}`,
        null,
        completed,
        `${stepKey} setup ${completed ? 'marked complete' : 'unchecked'}`
      )

      logger.info('[ShowService] Setup checklist updated', { showId, stepKey, completed })
    } catch (error) {
      logger.error('[ShowService] Failed to update setup checklist', { error: error.message, showId, stepKey })
      throw error
    }
  }

  // ─── Activity logging ───────────────────────────

  /**
   * Log activity for a show (follows projectService.logActivity pattern)
   */
  async logActivity(showId, action, field, oldValue, newValue, note = null) {
    try {
      const user = auth.currentUser
      const logsRef = collection(db, this.collection, showId, 'activityLogs')

      let userType = 'system'
      if (user) {
        const idTokenResult = await user.getIdTokenResult()
        const claims = idTokenResult.claims

        if (claims.admin || claims.super) {
          userType = 'admin'
        } else if (claims.user) {
          userType = 'client'
        }
      }

      const logEntry = {
        showId,
        action,
        field: field || null,
        oldValue: oldValue !== undefined ? oldValue : null,
        newValue: newValue !== undefined ? newValue : null,
        userId: user?.uid || 'system',
        userName: user?.displayName || user?.email || 'System',
        userType,
        timestamp: serverTimestamp(),
        note: note || null
      }

      await addDoc(logsRef, logEntry)
      logger.info('[ShowService] Activity logged', { showId, action })
    } catch (error) {
      // Don't throw — logging failures shouldn't break the main operation
      logger.error('[ShowService] Failed to log activity', { error: error.message, showId })
    }
  }

  /**
   * Subscribe to activity logs for a show (real-time, newest first)
   */
  subscribeToShowActivity(showId, callback) {
    const q = query(
      collection(db, this.collection, showId, 'activityLogs'),
      orderBy('timestamp', 'desc')
    )
    return onSnapshot(
      q,
      (snapshot) => {
        const logs = []
        snapshot.forEach(docSnap => {
          const data = docSnap.data()
          logs.push({
            id: docSnap.id,
            ...data,
            timestamp: data.timestamp?.toMillis ? data.timestamp.toMillis() : data.timestamp
          })
        })
        callback(logs)
      },
      (error) => {
        logger.error('[ShowService] Activity logs snapshot error', { error: error.message, showId })
      }
    )
  }
}

export const showService = new ShowService()
